/*
 * rheincool — Stromverbrauchs-Rechner (Stromverbrauch-Handoff.md)
 * kWh/Tag = kW × Auslastungsfaktor(Effizienzklasse) × h/Tag,
 * € über den zentralen Strompreis aus #rc-constants (eine Quelle!).
 * Vergleichszeile: Ersparnis A+++ gegenüber A+ pro Sommer.
 * mini-Variante (EnergyCostMiniCalc): nur Leistung + Stunden, eine
 * Kennzahl €/Sommer. Store-Key 'energy'.
 */
(() => {
  'use strict';
  if (customElements.get('rc-energy-calculator')) return;

  class RcEnergyCalculator extends window.RcCalculator {
    init() {
      this.storeKey = 'energy';
      this.trackPrefix = 'energy_calc';
      this.lastSignature = null;

      this.els = {
        day: this.querySelector('[data-energy-day]'),
        month: this.querySelector('[data-energy-month]'),
        summer: this.querySelector('[data-energy-summer]'),
        compare: this.querySelector('[data-energy-compare]'),
        note: this.querySelector('[data-energy-note]'),
        hoursLabel: this.querySelector('[data-calc-label-value="hours"]'),
        stickyValue: this.querySelector('[data-energy-sticky-value]'),
        ctas: Array.from(this.querySelectorAll('[data-energy-cta]')),
      };

      /* Strompreis aus der zentralen Konstante vorbefüllen */
      const priceField = this.querySelector('[data-calc-field="price"]');
      if (priceField && priceField.value === '') {
        priceField.value = this.defaultPrice();
      }
      if (priceField) {
        priceField.addEventListener('input', () => this.update());
      }

      this.renderNote(this.currentPrice());

      this.els.ctas.forEach((cta) => {
        cta.addEventListener('click', () => {
          const result = window.rcStore?.get('energy');
          this.track('saving_cta_click', {
            eurSommer: result ? Math.round(result.eurSommer) : null,
            ersparnis: result ? Math.round(result.ersparnis) : null,
          });
        });
      });
    }

    fill(template, values) {
      return Object.keys(values).reduce(
        (text, key) => text.split(`[${key}]`).join(values[key]),
        template || ''
      );
    }

    defaultPrice() {
      return typeof this.constants.energyPriceCtKwh === 'number' ? this.constants.energyPriceCtKwh : 35;
    }

    currentPrice() {
      if (this.config.mini) return this.defaultPrice();
      const price = this.fieldValue('price');
      return Number.isNaN(price) || price <= 0 ? this.defaultPrice() : price;
    }

    renderNote(price) {
      if (!this.els.note) return;
      const s = this.config.strings || {};
      let note = this.fill(s.note, { price: this.formatNumber(price, price % 1 === 0 ? 0 : 1) });
      if (this.constants.energyPriceStand) {
        note += ` · ${this.fill(s.noteStand, { stand: this.constants.energyPriceStand })}`;
      }
      this.els.note.textContent = note;
    }

    compute() {
      const factors = this.config.factors || {};
      const kw = parseFloat(this.segmentValue('power')) || 2.5;
      const cls = this.config.mini ? 'app' : this.segmentValue('class') || 'app';
      const hours = this.fieldValue('hours');
      if (Number.isNaN(hours) || hours <= 0) return null;
      const price = this.currentPrice();
      const factor = factors[cls];
      if (!factor) return null;

      const sommerTage = typeof this.constants.sommerTage === 'number' ? this.constants.sommerTage : 90;
      const kwhTag = kw * factor * hours;
      const eurTag = (kwhTag * price) / 100;
      const ersparnis = Math.max(
        0,
        ((kw * hours * (factors.ap - factors.appp) * sommerTage * price) / 100) || 0
      );

      return {
        eurTag,
        eurMonat: eurTag * 30,
        eurSommer: eurTag * sommerTage,
        ersparnis,
        kw,
        klasse: cls,
        hours,
        price,
      };
    }

    renderResult(result) {
      const s = this.config.strings || {};
      const summerText = this.formatEuro(result.eurSommer, 0);

      if (this.els.hoursLabel) this.els.hoursLabel.textContent = `${this.formatNumber(result.hours, 0)} h`;
      if (this.els.day) this.els.day.textContent = this.formatEuro(result.eurTag, 2);
      if (this.els.month) this.els.month.textContent = this.formatEuro(result.eurMonat, 0);
      if (this.els.summer) this.els.summer.textContent = summerText;
      if (this.els.compare) {
        this.els.compare.textContent = this.fill(s.compare, {
          x: this.formatNumber(result.ersparnis, 0),
        });
      }
      if (this.els.stickyValue) {
        this.els.stickyValue.textContent = this.fill(s.stickySummer, {
          x: this.formatNumber(result.eurSommer, 0),
        });
      }
      this.renderNote(result.price);
      this.announce(this.fill(s.announce, { x: this.formatNumber(result.eurSommer, 0) }));

      const signature = `${Math.round(result.eurSommer)}|${Math.round(result.ersparnis)}`;
      if (signature !== this.lastSignature) {
        this.lastSignature = signature;
        this.track('energy_calc_result', {
          eurSommer: Math.round(result.eurSommer),
          ersparnis: Math.round(result.ersparnis),
          kw: result.kw,
          klasse: result.klasse,
          h: result.hours,
          preis: result.price,
        });
      }
    }

    trackParams() {
      return {
        kw: parseFloat(this.segmentValue('power')) || 2.5,
        klasse: this.config.mini ? 'app' : this.segmentValue('class') || 'app',
        h: this.fieldValue('hours'),
        preis: this.currentPrice(),
      };
    }
  }

  customElements.define('rc-energy-calculator', RcEnergyCalculator);
})();
